/**
 * S4 — pure helpers for attendance display on the Scheduling Board drawer (Final PRD §13, §31–32, §48). No API access.
 */
export const OUTCOME_LABEL = {
  DELIVERED: 'Delivered', PARTIAL: 'Partially delivered', NOT_DELIVERED: 'Not delivered', CANCELLED: 'Cancelled',
};

export const outcomeLabel = (outcome) => (outcome ? OUTCOME_LABEL[outcome] || outcome : 'Not yet recorded');

const hhmm = (iso) => (iso ? String(iso).slice(11, 16) : '—');

/** "scheduled vs actual" for one member participant from the group's coverage_evidence entry. */
export const participantAttendanceLine = (ev) => {
  const sched = `${hhmm(ev?.scheduled?.start)}–${hhmm(ev?.scheduled?.end)}`;
  if (!ev?.attendance_status) return { scheduled: sched, actual: 'not recorded', status: 'UNRECORDED' };
  if (ev.attendance_status === 'NO_SHOW') return { scheduled: sched, actual: 'did not attend', status: 'NO_SHOW' };
  return { scheduled: sched, actual: `${hhmm(ev.actual?.start)}–${hhmm(ev.actual?.end)}`, status: ev.attendance_status };
};

/** Clock state of a worker assignment: NOT_CLOCKED | CLOCKED_IN | CLOCKED_OUT (OPEN coverage has no clock). */
export const clockState = (assignment) => {
  if (!assignment?.worker_id) return 'NO_WORKER';
  const att = assignment.attendance || {};
  if (att.check_out_time) return 'CLOCKED_OUT';
  if (att.check_in_time) return 'CLOCKED_IN';
  return 'NOT_CLOCKED';
};

export const clockLabel = (assignment, tzFormatter) => {
  const att = assignment?.attendance || {};
  const f = tzFormatter || ((iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''));
  switch (clockState(assignment)) {
    case 'CLOCKED_OUT': return `clocked ${f(att.check_in_time)} → ${f(att.check_out_time)}`;
    case 'CLOCKED_IN': return `clocked in ${f(att.check_in_time)} (on shift)`;
    case 'NOT_CLOCKED': return 'not clocked';
    default: return '';
  }
};

/** Business-rule text for the S4 codes (falls back to the shared-delivery / segment messages elsewhere). */
const MESSAGES = {
  REASON_REQUIRED: 'A reason is required when clocking on behalf of the worker.',
  NOT_ASSIGNMENT_WORKER: 'Only the assigned worker (or a roster manager with a reason) can clock this assignment.',
  ALREADY_CLOCKED_IN: 'This assignment is already clocked in.',
  ALREADY_CLOCKED_OUT: 'This assignment is already clocked out.',
  NOT_CLOCKED_IN: 'Clock in before clocking out.',
  INVALID_CLOCK_TIME: 'Clock time must be a valid time after the clock-in.',
  SHIFT_LOCKED: 'Delivery has started or attendance is recorded — use the correction path with a reason.',
};
export const attendanceErrorMessage = (err) => {
  const d = err?.response?.data?.detail || err?.detail || {};
  const code = typeof d === 'string' ? d : d?.code;
  return (code && MESSAGES[code]) || d?.message || code || 'Attendance action failed';
};

// ── UA-8 F34 / F35 (office sheet) ─────────────────────────────────────────────
/** Office clock target: one worker ⇒ that worker; several (2:1) ⇒ a selection is required. */
export const clockTargetRule = (assignedStaff = [], selectedId = '') => {
  const ids = (assignedStaff || []).map((s) => String(s.id));
  if (ids.length === 0) return { ok: false, staffId: null, reason: 'No worker is assigned to this shift.' };
  if (ids.length === 1) return { ok: true, staffId: ids[0], reason: '' };
  if (selectedId && ids.includes(String(selectedId))) return { ok: true, staffId: String(selectedId), reason: '' };
  return { ok: false, staffId: null, reason: 'More than one worker is assigned (2:1) — select the worker this clock is for.' };
};

export const OFFICE_ATTENDANCE_REASONS = [
  ['PARTICIPANT_LEFT_EARLY', 'Participant left early'], ['PARTICIPANT_ARRIVED_LATE', 'Participant arrived late'], ['PARTICIPANT_UNWELL', 'Participant unwell'],
  ['PARTICIPANT_NOT_HOME', 'Participant not home'], ['PARTICIPANT_DECLINED', 'Participant declined the service'], ['FAMILY_CANCELLED_AT_DOOR', 'Family cancelled at the door'], ['OTHER', 'Other'],
];

/** Office participant-attendance form → request body for POST /shifts/{id}/attendance, or {error}. */
export const officeAttendanceBody = ({ participantId, status, date, start, end, reason, notes }) => {
  if (status === 'NO_SHOW' || status === 'PARTIAL') {
    if (!reason) return { error: 'Select a reason for partial delivery / not delivered.' };
    if (!(notes || '').trim()) return { error: 'A short written reason is required.' };
  }
  const body = { participant_id: participantId, status, reason_code: reason || null, notes: (notes || '').trim() || null };
  if (status !== 'NO_SHOW') {
    if (!start || !end) return { error: 'Actual start and end are required.' };
    if (end <= start) return { error: 'Actual end must be after the actual start.' };
    body.actual_start = `${date}T${start}:00`; body.actual_end = `${date}T${end}:00`;
  }
  return { body };
};
